import { Box } from '@cdl/heartbeat-react';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectedClusterIdsSelector } from '../../selectors/cluster.selectors';
import { BaseClusterInformation } from './BaseClusterInfo';
import { squareToPixel } from '../../../plot/utils/graph.utils';
import { Cluster } from '../../clustering.types';
import { selectBaseClusters } from '../../thunks/select-base-clusters.thunk';

interface Props {
  cluster: Cluster;
}

const WIDTH = squareToPixel(12);
const HEIGHT = squareToPixel(4);
export const CONTAINER_WIDTH = WIDTH + 4;
export const CONTAINER_HEIGHT = HEIGHT + 4;
export const MARGIN_RIGHT = 8;

export const BaseCluster = forwardRef<HTMLDivElement, Props>(
  ({ cluster }, ref) => {
    const dispatch = useDispatch();
    const selectedIds = useSelector(selectedClusterIdsSelector);

    const selectBaseCluster = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(
          selectBaseClusters(cluster, e.shiftKey, e.ctrlKey || e.metaKey),
        );
      },
      [cluster, dispatch],
    );

    const borderColor = useMemo(
      () => (selectedIds.has(cluster.id) ? 'primary.500' : 'neutral.300'),
      [selectedIds, cluster.id],
    );

    return (
      <Box
        height={CONTAINER_HEIGHT}
        width={CONTAINER_WIDTH}
        cursor='pointer'
        ref={ref}
        display='inline-block'
        bgColor='transparent'
        border={'0.125rem solid'}
        borderColor={borderColor}
        borderRadius='0.125rem'
        mr='s'
        position='relative'
        onClick={selectBaseCluster}
        data-testid='base-cluster'
      >
        <BaseClusterInformation cluster={cluster} />
        {!selectedIds.has(cluster.id) && (
          <Box
            data-testid='gray-base-cluster'
            position='absolute'
            bgColor='black'
            opacity='0.4'
            width='100%'
            height='100%'
          />
        )}
      </Box>
    );
  },
);
